import { HORIZON_Y, GROUND_Y, CENTER_X, getScale, getSpeedMultiplier } from './constants';

export interface TreeConfig {
    spawnX: number;
    lane: number;
    isDecor?: boolean;
}

export default class Tree {
    readonly spriteImg: HTMLImageElement;

    // Base sprite size (100x100 from asset)
    readonly baseWidth = 100;
    readonly baseHeight = 100;

    // Position and perspective
    pos: { x: number; y: number };
    readonly initialX: number;
    readonly lane: number;

    // Scale: 0.15 at horizon, 1.0 at ground
    scale: number = 0.15;

    // Track if already hit by player
    hasCollided: boolean = false;

    // Decor trees are larger and don't collide
    readonly isDecor: boolean;
    readonly sizeMultiplier: number;

    constructor(config: TreeConfig, sharedImg: HTMLImageElement) {
        this.spriteImg = sharedImg;
        this.initialX = config.spawnX;
        this.pos = { x: config.spawnX, y: HORIZON_Y };
        this.lane = config.lane;
        this.isDecor = config.isDecor ?? false;
        this.sizeMultiplier = this.isDecor ? 1.8 : 1;
    }

    update(deltaTime: number, skierSpeed: number) {
        this.scale = getScale(this.pos.y);

        const speedMultiplier = getSpeedMultiplier(this.scale);
        const descentSpeed = skierSpeed * 10 * speedMultiplier;
        this.pos.y += descentSpeed * deltaTime / 1000;

        // Parallax: linear spread to match track shape
        const baseOffset = this.initialX - CENTER_X;
        const spreadFactor = 1 + (this.scale - 0.15) * 10;
        this.pos.x = CENTER_X + baseOffset * spreadFactor;
    }

    isOffScreen(): boolean {
        return this.pos.y > GROUND_Y + 70;
    }

    getCollisionBox(): { x: number; y: number; width: number; height: number } {
        const width = this.baseWidth * this.scale * 0.3;
        const height = this.baseHeight * this.scale * 0.5;
        return {
            x: this.pos.x - width / 2,
            y: this.pos.y - height,
            width,
            height
        };
    }

    draw(ctx: CanvasRenderingContext2D, zoom: number) {
        if (!this.spriteImg.complete) return;

        const drawWidth = this.baseWidth * this.scale * this.sizeMultiplier;
        const drawHeight = this.baseHeight * this.scale * this.sizeMultiplier;

        ctx.drawImage(
            this.spriteImg,
            0, 0,
            this.baseWidth, this.baseHeight,
            zoom * (this.pos.x - drawWidth / 2),
            zoom * (this.pos.y - drawHeight),
            zoom * drawWidth,
            zoom * drawHeight
        );
    }
}
